import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import COLORS from "../Helpers/colors";
import "./BreadCrumb.css"
import { Link } from "react-router-dom";
import { useIsSmallScreen } from "../hooks/useIsSmallScreen";

export interface BreadcrumbItem {
    text: string;
    link?: string;
}

interface BreadcrumbProps {
    items?: BreadcrumbItem[];
    rootItem?: BreadcrumbItem | null; // Optional root item override, null to skip root
}

export const BreadcrumbComponent: React.FC<BreadcrumbProps> = ({ items = [], rootItem }) => {
    const navRef = useRef<HTMLElement>(null);

    // On phones the bar sticks under the top bar (BreadCrumb.css) and slides
    // out of view while the page scrolls down, back in as soon as it scrolls up.
    const isSmallScreen = useIsSmallScreen();
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        setHidden(false);
        if (!isSmallScreen) return;
        const scroller = navRef.current?.closest('.main-content');
        if (!scroller) return;
        let lastTop = scroller.scrollTop;
        const handleScroll = () => {
            const top = scroller.scrollTop;
            const delta = top - lastTop;
            // Ignore small movements so the bar doesn't flicker
            if (Math.abs(delta) < 8) return;
            lastTop = top;
            const barHeight = navRef.current?.offsetHeight ?? 44;
            setHidden(delta > 0 && top > barHeight);
        };
        scroller.addEventListener('scroll', handleScroll, { passive: true });
        return () => scroller.removeEventListener('scroll', handleScroll);
    }, [isSmallScreen]);

    // Use custom root item if provided, default to 'Byggnader', or skip if rootItem is null
    const defaultRoot: BreadcrumbItem = {
        text: 'Byggnader',
        link: '/map'
    };

    const allItems = rootItem === null
        ? items
        : [rootItem ?? defaultRoot, ...items];

    // On small screens the bar scrolls sideways (see BreadCrumb.css). Start at
    // the end so the current page is visible; the ancestors are a swipe away.
    const trailKey = allItems.map((item) => item.text).join('/');
    useLayoutEffect(() => {
        const nav = navRef.current;
        if (nav) nav.scrollLeft = nav.scrollWidth;
    }, [trailKey]);

    return (
        <nav ref={navRef} className={`breadcrumbs${hidden ? ' breadcrumbs--hidden' : ''}`} aria-label="Sökväg" style={{
            backgroundColor: COLORS.breadcrumbBackground,
        }}>
            {allItems.map((item, index) => (
                <React.Fragment key={index}>
                    {
                        item.link ?
                            <Link to={item.link} className="breadcrumb-item">
                                {item.text}
                            </Link>
                            :
                            <span className="breadcrumb-item-current" aria-current={index === allItems.length - 1 ? 'page' : undefined}> {item.text} </span>
                    }
                    {index < allItems.length - 1 && (
                        <span className="breadcrumb-item-separator"><img src={"/Images/button-icons/breadcrumb-separator.png"} alt="" /></span>
                    )}
                </React.Fragment>
            ))}
        </nav>
    );
};
